/**
 * Graphics-tier segmented control. Lifted verbatim from the old single-panel
 * Landing.ts. A tier change forces a full game rebuild (PostFX is not
 * hot-swappable — `.claude/rules/architecture.md`), which main.ts handles via
 * the onChangeTier callback.
 */
import type { QualityTier } from '../../engine/Quality';

const STORAGE_KEY = 'extinctrum.quality';
const TIERS: QualityTier[] = ['low', 'medium', 'high', 'ultra'];
export type Choice = 'auto' | QualityTier;

export function readChoice(): Choice {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'auto' || (v && (TIERS as string[]).includes(v))) return v as Choice;
  } catch {
    /* private mode — no persistence */
  }
  return 'auto';
}

function writeChoice(c: Choice): void {
  try {
    localStorage.setItem(STORAGE_KEY, c);
  } catch {
    /* private mode — ignore */
  }
}

export interface GraphicsControl {
  readonly el: HTMLElement;
  choice(): Choice;
}

/**
 * @param detected  the tier the device heuristic picked, shown as the Auto hint
 * @param onChange  fired with the resolved tier (Auto → detected) on selection
 */
export function buildGraphics(
  detected: QualityTier,
  onChange: (tier: QualityTier) => void,
): GraphicsControl {
  let choice = readChoice();

  const wrap = el('div', 'landing-graphics');
  const label = el('div', 'landing-graphics-label');
  label.textContent = 'GRAPHICS';
  const row = el('div', 'landing-seg');
  row.setAttribute('role', 'radiogroup');
  row.setAttribute('aria-label', 'Graphics quality');

  const buttons = new Map<Choice, HTMLButtonElement>();
  const choices: Choice[] = ['auto', ...TIERS];
  for (const c of choices) {
    const b = el('button', 'landing-seg-btn') as HTMLButtonElement;
    b.type = 'button';
    b.setAttribute('role', 'radio');
    b.textContent = c === 'auto' ? `Auto (${detected})` : c[0].toUpperCase() + c.slice(1);
    const selected = c === choice;
    b.setAttribute('aria-checked', String(selected));
    if (selected) b.classList.add('on');
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      if (c === choice) return;
      choice = c;
      writeChoice(c);
      for (const [key, btn] of buttons) {
        const on = key === c;
        btn.classList.toggle('on', on);
        btn.setAttribute('aria-checked', String(on));
      }
      onChange(c === 'auto' ? detected : c);
    });
    buttons.set(c, b);
    row.appendChild(b);
  }

  wrap.append(label, row);
  return { el: wrap, choice: () => choice };
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  n.className = className;
  return n;
}
